import React, { useState } from 'react';
import axios from 'axios';
import './CreditApplicator.css';

const CreditApplicator = () => {
  const [fileData, setFileData] = useState(null);
  const [fileName, setFileName] = useState('');
  const [validationError, setValidationError] = useState(null);
  const [passkey, setPasskey] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [amountToApply, setAmountToApply] = useState('');
  const [paymentId, setPaymentId] = useState('');
  const [creditType, setCreditType] = useState('excess-payment');

  const serverURL = process.env.REACT_APP_SERVER_BASE_URL;

  const validateCredits = (data) => {
    if (!Array.isArray(data)) {
      return 'File must contain a JSON array.';
    }
    if (data.length === 0) {
      return 'Array is empty — nothing to process.';
    }
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      if (!item.invoice_id) return `Item at index ${i} is missing "invoice_id".`;
      if (item.amount_applied == null || isNaN(Number(item.amount_applied))) {
        return `Item at index ${i} has an invalid or missing "amount_applied".`;
      }
    }
    return null;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setResult(null);

    if (!file) {
      setFileData(null);
      setFileName('');
      setValidationError(null);
      return;
    }

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Convert large numeric invoice_id to strings before parsing
        // to prevent JavaScript losing precision on IDs exceeding Number.MAX_SAFE_INTEGER
        let rawText = e.target.result;
        rawText = rawText.replace(/"invoice_id"\s*:\s*(\d+)/g, '"invoice_id": "$1"');
        const parsed = JSON.parse(rawText);
        const error = validateCredits(parsed);
        if (error) {
          setValidationError(error);
          setFileData(null);
        } else {
          setValidationError(null);
          setFileData(parsed);
          const fileTotal = parsed.reduce((sum, item) => sum + Number(item.amount_applied), 0);
          setAmountToApply(fileTotal.toFixed(2));
        }
      } catch {
        setValidationError('Invalid JSON file. Could not parse contents.');
        setFileData(null);
      }
    };
    reader.readAsText(file);
  };

  const totalAmount = fileData
    ? fileData.reduce((sum, item) => sum + Number(item.amount_applied), 0)
    : 0;

  const uniqueInvoices = fileData
    ? new Set(fileData.map((item) => item.invoice_id)).size
    : 0;

  const handleProcess = async () => {
    if (!fileData || !passkey) return;
    setIsProcessing(true);
    setResult(null);

    try {
      const response = await axios.post(`${serverURL}/apply-credits`, {
        passkey,
        credits: fileData,
        amount_to_apply: Number(amountToApply),
        payment_id: paymentId,
        credit_type: creditType,
      });
      setResult(response.data);
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Unknown error';
      setResult({ success: false, error: msg });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="credit-applicator">
      <h2>Apply Credits to Invoices</h2>

      <div className="ca-section">
        <label htmlFor="ca-passkey">Passkey:</label>
        <input
          type="password"
          id="ca-passkey"
          value={passkey}
          onChange={(e) => setPasskey(e.target.value)}
          placeholder="Enter webhook passkey"
          className="ca-input"
        />
      </div>

      <div className="ca-section">
        <label htmlFor="ca-credit-type">Credit Type:</label>
        <select
          id="ca-credit-type"
          value={creditType}
          onChange={(e) => setCreditType(e.target.value)}
          className="ca-input"
        >
          <option value="excess-payment">Excess Payment</option>
          <option value="credit-note">Credit Note</option>
        </select>
      </div>

      <div className="ca-section">
        <label htmlFor="ca-payment-id">
          {creditType === 'credit-note' ? 'Credit Note ID:' : 'Payment ID:'}
        </label>
        <input
          type="text"
          id="ca-payment-id"
          value={paymentId}
          onChange={(e) => setPaymentId(e.target.value)}
          placeholder={creditType === 'credit-note' ? 'Enter credit note ID' : 'Enter payment ID'}
          className="ca-input"
        />
      </div>

      <div className="ca-section">
        <label htmlFor="ca-file">Upload JSON File:</label>
        <input
          type="file"
          id="ca-file"
          accept=".json"
          onChange={handleFileChange}
        />
      </div>

      {validationError && (
        <div className="ca-error">
          <strong>Validation Error:</strong> {validationError}
        </div>
      )}

      {fileData && (
        <div className="ca-summary">
          <h3>File Summary — {fileName}</h3>
          <table className="ca-table">
            <tbody>
              <tr>
                <td><strong>Total Records</strong></td>
                <td>{fileData.length}</td>
              </tr>
              <tr>
                <td><strong>Unique Invoices</strong></td>
                <td>{uniqueInvoices}</td>
              </tr>
              <tr>
                <td><strong>Total Amount in File</strong></td>
                <td>${totalAmount.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <div className="ca-section">
            <label htmlFor="ca-amount">Amount to Apply (max ${totalAmount.toFixed(2)}):</label>
            <input
              type="number"
              id="ca-amount"
              value={amountToApply}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || Number(val) <= totalAmount) {
                  setAmountToApply(val);
                }
              }}
              min="0"
              max={totalAmount}
              step="0.01"
              className="ca-input"
            />
          </div>

          <button
            onClick={handleProcess}
            disabled={isProcessing || !passkey || !paymentId || !amountToApply || Number(amountToApply) <= 0}
            className="ca-process-btn"
          >
            {isProcessing ? 'Processing...' : 'Process'}
          </button>
        </div>
      )}

      {result && (
        <div className={`ca-result ${result.success ? 'ca-result-success' : 'ca-result-failure'}`}>
          <h3>Processing Result</h3>

          {result.error && !result.results && (
            <p className="ca-error"><strong>Error:</strong> {result.error}</p>
          )}

          {result.results && (
            <>
              <table className="ca-table">
                <tbody>
                  <tr>
                    <td><strong>Total Invoices</strong></td>
                    <td>{result.total}</td>
                  </tr>
                  <tr>
                    <td><strong>Processed Successfully</strong></td>
                    <td>{result.processed}</td>
                  </tr>
                  <tr>
                    <td><strong>Skipped</strong></td>
                    <td>{result.skipped}</td>
                  </tr>
                  <tr>
                    <td><strong>Amount Applied</strong></td>
                    <td>${result.amount_applied?.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td><strong>Amount Remaining</strong></td>
                    <td>${result.amount_remaining?.toFixed(2)}</td>
                  </tr>
                  {result.last_error && (
                    <tr>
                      <td><strong>Error</strong></td>
                      <td className="ca-error-text">{result.last_error}</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <h4>Per-Invoice Details</h4>
              <table className="ca-table ca-details-table">
                <thead>
                  <tr>
                    <th>Invoice ID</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.map((r, idx) => (
                    <tr key={idx} className={r.success ? 'ca-row-success' : 'ca-row-failure'}>
                      <td>{r.invoice_id}</td>
                      <td>${r.amount?.toFixed(2)}</td>
                      <td>{r.success ? 'Success' : 'Failed'}</td>
                      <td>{r.error || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CreditApplicator;
